'use client';

import { usePathname } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useProject } from '@/contexts/ProjectContext';

export interface DetailPanelContextType {
  isOpen: boolean;
  content: ReactNode | null;
  contentType: string | null;
  openPanel: (content: ReactNode, type?: string) => void;
  closePanel: () => void;
}

const DetailPanelContext = createContext<DetailPanelContextType | undefined>(undefined);

interface DetailPanelProviderProps {
  children: ReactNode;
}

export function DetailPanelProvider({ children }: DetailPanelProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ReactNode | null>(null);
  const [contentType, setContentType] = useState<string | null>(null);
  const clearContentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathname = usePathname();
  const { currentProject } = useProject();
  const projectId = currentProject?.id || null;

  const clearPendingContentReset = useCallback(() => {
    if (clearContentTimeoutRef.current) {
      clearTimeout(clearContentTimeoutRef.current);
      clearContentTimeoutRef.current = null;
    }
  }, []);

  // Auto-close panel on route change OR project change.
  // Project switching does not change pathname (e.g. still /app/pages), so we must reset here
  // to avoid showing stale content from a previous project.
  useEffect(() => {
    clearPendingContentReset();
    setIsOpen(false);
    setContent(null);
    setContentType(null);
  }, [clearPendingContentReset, pathname, projectId]);

  useEffect(() => {
    return () => clearPendingContentReset();
  }, [clearPendingContentReset]);

  const openPanel = useCallback((newContent: ReactNode, type: string = 'default') => {
    clearPendingContentReset();
    setContent(newContent);
    setContentType(type);
    setIsOpen(true);
  }, [clearPendingContentReset]);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    // Clear content after transition completes
    clearContentTimeoutRef.current = setTimeout(() => {
      setContent(null);
      setContentType(null);
      clearContentTimeoutRef.current = null;
    }, 250); // Match transition duration
  }, []);

  const value = useMemo(
    () => ({ isOpen, content, contentType, openPanel, closePanel }),
    [closePanel, content, contentType, isOpen, openPanel],
  );

  return (
    <DetailPanelContext.Provider value={value}>{children}</DetailPanelContext.Provider>
  );
}

export function useDetailPanel() {
  const context = useContext(DetailPanelContext);
  if (context === undefined) {
    throw new Error('useDetailPanel must be used within a DetailPanelProvider');
  }
  return context;
}
